/**
 * FundDetailModal — detailed research view for a selected mutual fund.
 *
 * Shows:
 * - NAV chart (with rolling returns overlay option)
 * - Benchmark selector + "Compute" button
 * - Metrics panel: CAGR, Sharpe, Beta, Alpha, Downside Capture, Std Dev, R²
 * - Transparency: marks unavailable metrics (PE, earnings growth) and shows assumptions
 * - TER display (from TerProvider or status message)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    getFundNav,
    getFundAnalytics,
    getFundTer,
    getBenchmarksList,
} from '../../services/fundsService';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LineChart,
    Line,
} from 'recharts';
import '../../styles/FundsPage.css';

const unwrap = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : res);

/** Format a metric value nicely */
const fmt = (val, suffix = '') => {
    if (val == null) return null;
    return typeof val === 'number' ? `${val}${suffix}` : String(val);
};

const FundDetailModal = ({ fund, onClose }) => {
    const schemeCode = fund.schemeCode;

    // ── NAV chart state ──
    const [navData, setNavData] = useState([]);
    const [navLoading, setNavLoading] = useState(true);
    const [meta, setMeta] = useState(fund);

    // ── Benchmarks ──
    const [benchmarks, setBenchmarks] = useState([]);
    const [selectedBenchmark, setSelectedBenchmark] = useState('');

    // ── Analytics ──
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    // ── TER ──
    const [ter, setTer] = useState(null);

    // ── Rolling returns view ──
    const [showRolling, setShowRolling] = useState(false);

    // Fetch NAV + TER + benchmarks on mount
    useEffect(() => {
        let cancelled = false;

        // Fetch NAV
        getFundNav(schemeCode)
            .then((raw) => {
                if (cancelled) return;
                const result = unwrap(raw);
                if (result?.data) {
                    // Convert to chart format (ascending order)
                    const series = result.data
                        .map((d) => ({
                            date: d.date,
                            nav: parseFloat(d.nav),
                            // Sort key (DD-MM-YYYY → sortable)
                            _sort: d.date.split('-').reverse().join('-'),
                        }))
                        .sort((a, b) => a._sort.localeCompare(b._sort));
                    setNavData(series);
                    if (result.meta) setMeta((prev) => ({ ...prev, ...result.meta }));
                }
            })
            .catch((err) => console.error('NAV fetch failed:', err))
            .finally(() => { if (!cancelled) setNavLoading(false); });

        // Fetch TER
        getFundTer(schemeCode)
            .then((raw) => {
                if (!cancelled) setTer(unwrap(raw));
            })
            .catch(() => setTer({ available: false, message: 'TER source not configured yet (AMFI ingestion pending)' }));

        // Fetch benchmark list
        getBenchmarksList()
            .then((raw) => {
                if (!cancelled) {
                    const list = unwrap(raw);
                    setBenchmarks(Array.isArray(list) ? list : []);
                }
            })
            .catch(() => { });

        return () => { cancelled = true; };
    }, [schemeCode]);

    // ── Compute analytics ──
    const computeAnalytics = useCallback(async () => {
        setAnalyticsLoading(true);
        try {
            const raw = await getFundAnalytics(schemeCode, selectedBenchmark);
            const data = unwrap(raw);
            setAnalytics(data);
        } catch (err) {
            console.error('Analytics computation failed:', err);
            setAnalytics({ error: 'Failed to compute analytics. Please try again.' });
        } finally {
            setAnalyticsLoading(false);
        }
    }, [schemeCode, selectedBenchmark]);

    // Auto-compute when benchmark changes (if one is selected)
    useEffect(() => {
        // Compute on mount (without benchmark for non-benchmark metrics)
        computeAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const formatNavDate = (dateStr) => {
        if (!dateStr) return '';
        // DD-MM-YYYY format from mfapi
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            const d = new Date(parts[2], parts[1] - 1, parts[0]);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
        }
        return dateStr;
    };

    // ── Build metrics list ──
    const metricsCards = analytics && !analytics.error ? [
        { label: '1Y CAGR', value: fmt(analytics.cagr_1y, '%'), note: null },
        { label: '3Y CAGR', value: fmt(analytics.cagr_3y, '%'), note: null },
        { label: '5Y CAGR', value: fmt(analytics.cagr_5y, '%'), note: null },
        { label: '10Y CAGR', value: fmt(analytics.cagr_10y, '%'), note: analytics.cagr_10y == null ? 'Insufficient history' : null },
        { label: 'Std Dev (ann.)', value: fmt(analytics.std_dev, '%'), note: null },
        { label: 'Sharpe Ratio', value: fmt(analytics.sharpe), note: `Risk-free: ${analytics.assumptions?.risk_free_rate || '7%'}` },
        { label: 'Beta', value: fmt(analytics.beta), note: analytics.beta == null ? 'Select benchmark to compute' : `vs ${analytics.assumptions?.benchmark_symbol || 'benchmark'}` },
        { label: 'Alpha', value: fmt(analytics.alpha, '%'), note: analytics.alpha == null ? 'Select benchmark to compute' : `vs ${analytics.assumptions?.benchmark_symbol || 'benchmark'}` },
        { label: 'R²', value: fmt(analytics.r_squared), note: analytics.r_squared == null ? 'Select benchmark to compute' : null },
        { label: 'Downside Capture', value: fmt(analytics.downside_capture, '%'), note: analytics.downside_capture == null ? 'Select benchmark to compute' : 'Target: < 80-90%' },
        { label: 'TER', value: ter?.available ? `${ter.ter}%` : null, note: ter?.available ? `Source: ${ter.source || 'AMFI'}` : (ter?.message || 'TER source not configured yet (AMFI ingestion pending)'), unavailable: !ter?.available },
        { label: 'Earnings Growth', value: null, note: analytics.earnings_growth_note, unavailable: true },
        { label: 'PE of Holdings', value: null, note: analytics.pe_of_holdings_note, unavailable: true },
    ] : [];

    return (
        <div className="fund-detail-overlay" onClick={handleOverlayClick}>
            <div className="fund-detail-panel" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="fund-detail-header">
                    <div>
                        <h2 className="fund-detail-title">
                            {meta?.schemeName || meta?.scheme_name || `Scheme ${schemeCode}`}
                        </h2>
                        <p className="fund-detail-meta">
                            {meta?.fundHouse || meta?.fund_house || ''}{' '}
                            {meta?.schemeCategory || meta?.scheme_category ?
                                `| ${meta.schemeCategory || meta.scheme_category}` : ''}
                            {' '}| Code: {schemeCode}
                        </p>
                    </div>
                    <button className="fund-detail-close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                {/* Benchmark selector */}
                <div className="fund-benchmark-row">
                    <span className="fund-benchmark-label">Benchmark:</span>
                    <select
                        className="fund-benchmark-select"
                        value={selectedBenchmark}
                        onChange={(e) => setSelectedBenchmark(e.target.value)}
                    >
                        <option value="">None (metrics without benchmark)</option>
                        {benchmarks.map((b) => (
                            <option key={b.symbol} value={b.symbol}>
                                {b.name}
                            </option>
                        ))}
                    </select>
                    <button
                        className="fund-compute-btn"
                        onClick={computeAnalytics}
                        disabled={analyticsLoading}
                    >
                        {analyticsLoading ? 'Computing…' : 'Compute Analytics'}
                    </button>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-2)', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            checked={showRolling}
                            onChange={(e) => setShowRolling(e.target.checked)}
                        />
                        Show rolling returns
                    </label>
                </div>

                {/* NAV Chart */}
                <div className="fund-nav-chart">
                    {navLoading ? (
                        <div className="funds-loading" style={{ paddingTop: '6rem' }}>
                            Loading NAV chart…
                        </div>
                    ) : navData.length === 0 ? (
                        <div className="funds-empty">
                            <p>No NAV data available.</p>
                        </div>
                    ) : showRolling && analytics?.rolling_returns_3y?.length > 0 ? (
                        /* Rolling returns chart */
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={analytics.rolling_returns_3y}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={{ stroke: 'var(--border)' }}
                                    tickLine={false}
                                    minTickGap={50}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v) => `${v}%`}
                                    width={50}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: 'var(--bg-2)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 8,
                                        color: 'var(--text-1)',
                                        fontSize: '0.82rem',
                                    }}
                                    formatter={(val) => [`${val}%`, '3Y Rolling CAGR']}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="cagr"
                                    stroke="var(--brand)"
                                    strokeWidth={1.5}
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        /* NAV chart */
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={navData}>
                                <defs>
                                    <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--brand-2)" stopOpacity={0.25} />
                                        <stop offset="95%" stopColor="var(--brand-2)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatNavDate}
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={{ stroke: 'var(--border)' }}
                                    tickLine={false}
                                    minTickGap={50}
                                />
                                <YAxis
                                    domain={['auto', 'auto']}
                                    tick={{ fontSize: 11, fill: 'var(--text-2)' }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={55}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: 'var(--bg-2)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 8,
                                        color: 'var(--text-1)',
                                        fontSize: '0.82rem',
                                    }}
                                    labelFormatter={formatNavDate}
                                    formatter={(val) => [val?.toFixed(4), 'NAV']}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="nav"
                                    stroke="var(--brand-2)"
                                    strokeWidth={1.5}
                                    fillOpacity={1}
                                    fill="url(#navGrad)"
                                    dot={false}
                                    activeDot={{ r: 3 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Metrics Panel */}
                {analyticsLoading ? (
                    <div className="funds-loading">Computing analytics…</div>
                ) : analytics?.error ? (
                    <div className="funds-error">{analytics.error}</div>
                ) : metricsCards.length > 0 ? (
                    <>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0.75rem 0 0.5rem', color: 'var(--text-1)' }}>
                            Research Metrics
                        </h3>
                        <div className="fund-metrics-grid">
                            {metricsCards.map((m) => (
                                <div key={m.label} className="fund-metric-card">
                                    <span className="fund-metric-label">{m.label}</span>
                                    {m.unavailable || m.value == null ? (
                                        <span className="fund-metric-unavail">
                                            {m.note || 'Not available'}
                                        </span>
                                    ) : (
                                        <>
                                            <span className="fund-metric-value">{m.value}</span>
                                            {m.note && <span className="fund-metric-note">{m.note}</span>}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                ) : null}

                {/* Assumptions */}
                {analytics?.assumptions && !analytics.error && (
                    <div className="fund-assumptions">
                        <p className="fund-assumptions-title">Calculation Assumptions</p>
                        <p>Frequency: {analytics.assumptions.frequency}</p>
                        <p>Risk-free rate: {analytics.assumptions.risk_free_rate}</p>
                        <p>Benchmark: {analytics.assumptions.benchmark_symbol || 'None selected'}</p>
                        <p>Fund data range: {analytics.assumptions.fund_data_range}</p>
                        <p>Trading days/year: {analytics.assumptions.trading_days_per_year}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FundDetailModal;
